/**
 * What: Derives merchant-facing sync-history day states from the store sync status contract.
 * Transport / data-flow: Pure derivation over StoreSyncStatus coverage; no fetching,
 * no mutations. Per-day counts are not on this boundary, so hover carries date + state only.
 */
import type { StoreSyncStatus } from '@testfeed/shopify-contracts'

export type SyncHistoryDayState = 'verified' | 'collecting' | 'pending' | 'ancient' | 'before_history' | 'upcoming'

export type SyncHistoryDay = {
  date: string
  state: SyncHistoryDayState
}

export type SyncHistory = {
  days: SyncHistoryDay[]
  verified: number
  total: number
}

function eachDay(from: string, toExclusive: string): string[] {
  const days: string[] = []
  const cursor = new Date(`${from}T00:00:00.000Z`)
  const end = new Date(`${toExclusive}T00:00:00.000Z`)
  while (cursor < end) {
    days.push(cursor.toISOString().slice(0, 10))
    cursor.setUTCDate(cursor.getUTCDate() + 1)
  }
  return days
}

function endOfMonthKey(dayKey: string): string {
  const [year, month] = dayKey.split('-').map(Number)
  return new Date(Date.UTC(year as number, month as number, 0)).toISOString().slice(0, 10)
}

function historyFrameStart(windowStart: string): string {
  const date = new Date(`${windowStart}T00:00:00.000Z`)
  date.setUTCMonth(date.getUTCMonth() - 3, 1)
  return date.toISOString().slice(0, 10)
}

/**
 * Builds a six-month history frame: three earlier months, the collectable
 * window (published, accepted-but-unpublished, or missing), and trailing days
 * to month end (upcoming). Only the active publication is verified.
 */
export function buildSyncHistory(input: {
  coverage: NonNullable<StoreSyncStatus['coverage']>
  syncActive: boolean
  todayKey: string
}): SyncHistory {
  const windowStart = input.coverage.targetStart.slice(0, 10)
  const windowEnd = input.coverage.targetEnd.slice(0, 10)
  const missing = new Set(input.coverage.missingDays)
  const days: SyncHistoryDay[] = []
  const ancient = new Set(input.coverage.ancientDays)
  for (const date of eachDay(historyFrameStart(windowStart), windowStart)) {
    days.push({ date, state: ancient.has(date) ? 'ancient' : 'before_history' })
  }
  let verified = 0
  const published = new Set(input.coverage.publishedDays)
  for (const date of eachDay(windowStart, windowEnd)) {
    if (published.has(date)) {
      days.push({ date, state: 'verified' })
      verified += 1
    } else if (missing.has(date)) {
      days.push({ date, state: input.syncActive ? 'collecting' : 'pending' })
    } else {
      days.push({ date, state: 'pending' })
    }
  }
  const frameEnd = windowEnd > input.todayKey ? windowEnd : input.todayKey
  const upcomingFrom = frameEnd
  const monthEnd = new Date(`${endOfMonthKey(frameEnd)}T00:00:00.000Z`)
  monthEnd.setUTCDate(monthEnd.getUTCDate() + 1)
  for (const date of eachDay(windowEnd, monthEnd.toISOString().slice(0, 10))) {
    days.push({ date, state: date < upcomingFrom ? 'pending' : 'upcoming' })
  }
  return { days, verified, total: input.coverage.targetDays }
}

/** Formats an ISO timestamp as a merchant-facing "Sep 5" date, UTC. */
export function formatThroughDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' })
}
